/**
 * Accesos → Auditoría (Plan J, J3 — mockup 7).
 *
 * THE READ ENDPOINT EXISTS, AND THIS PAGE IS MOUNTED ON PURPOSE
 * -------------------------------------------------------------
 * Verified against the tree rather than assumed:
 * `builder/api/routes/access_audit.py` declares `GET` on
 * `/api/builder/admin/access-audit`, `builder/main.py` includes that
 * router, and the guard is `RequireManageUsers` — the same one
 * `admin_scopes.py` and `groups.py` use, because the trail is the
 * history of the tenant's access map and cannot be gated more loosely
 * than the map. `App.tsx` mounts this page at `/admin/access/audit`
 * behind `AdminRoute flag="manage_users"`, the front half of that guard.
 *
 * THE WARNING THAT USED TO BE HERE, IN THE PAST TENSE — THE RULE OUTLIVED
 * THE OCCASION
 * ----------------------------------------------------------------------
 * This header shipped for a while carrying a warning in capitals: the
 * handler did not exist, `service_access_audit` was the single WRITER of
 * a table nothing read back, and every request this page made answered
 * 404. The rule it stated is worth keeping now that its occasion is
 * gone, because the next audit-shaped screen will face the same choice.
 *
 * A screen that renders a trail must not ship ahead of the trail's
 * endpoint. Not because a 404 is untidy, but because of what the
 * Auditoría help text says to an operator, in Spanish and with
 * confidence: *"la fila se escribe en la misma transacción que el
 * cambio, así que una acción que no está acá no ocurrió"*. An empty
 * table under that sentence is not a missing feature, it is a false
 * statement — and the moment somebody reads this page is the moment they
 * are reconstructing an incident and least able to check it. Every other
 * honest refusal in this slice (the 503 rather than the stale file, the
 * 409 rather than the cross product) spends the credibility that one
 * screen would have burned.
 *
 * The page was written before the handler anyway, and that half held
 * too: the vocabulary it renders is real and closed, and
 * `__tests__/audit-actions.test.ts` reads
 * `service_access_audit._ACTION_TARGETS` out of the Python source and
 * fails when the two disagree. So writing the screen first PINNED the
 * shape the handler then had to answer, rather than inventing a second
 * one. Write the screen, pin the contract, ship the handler before the
 * route: that is the order, and the interval where the route was live
 * with nothing behind it is the part not to repeat.
 *
 * PORTED FROM THE PLATFORM CONSOLE'S `AuditPage`, WHICH ALREADY SOLVED
 * THIS
 * -------------------------------------------------------------------
 * * **Nothing is filtered in the browser.** A filtered view that
 *   silently hides rows the server would have returned is how an
 *   incident reconstruction misses the row that mattered.
 * * **The limit box REPORTS an out-of-range value** instead of quietly
 *   substituting a default, and the at-cap footer admits the trail
 *   probably continues.
 * * **The action dictionary is closed** and an unknown action keeps its
 *   RAW value in a warning chip — an event nobody can name is exactly
 *   the row an audit reader needs to see as unusual, not as a tidy
 *   label.
 *
 * `actor_email` is the column and `actor_user_id` is detail, because
 * migration 054 stores the address NOT NULL beside a nullable FK
 * precisely so the line survives the person. `before_json` /
 * `after_json` are nullable ON PURPOSE — a create has no before, a
 * delete has no after — so the modal says "no existía" and never renders
 * an empty object, which would be an invented value.
 */

import { Button, Input, Modal } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  DateCell,
  EmptyState,
  PageContainer,
  PageHeader,
  Section,
  type Column,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { resolveLocalized, type DisplayLabel } from "@pervasivemind/dashboard-render";
import { Calculator, Filter, ScrollText } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import {
  ESTIMATE_BASES,
  accessAuditApi,
  type AccessAuditOut,
} from "@/lib/access-api";
import { projectsApi } from "@/lib/builder-api";
import { parseProjectName } from "@/lib/project-name";
import { DictChip } from "@/pages/admin-access/AccessCells";
import { AccessTabs } from "@/pages/admin-access/AccessTabs";
import { actorKindDict, auditActionDict } from "@/pages/admin-access/access-status";
import {
  InsightCostTable,
  RunsPerMonthField,
  currentEstimate,
  currentMonthlyCost,
  estimateBasisDict,
  loadProjectEstimates,
  parseRunsPerMonth,
  splitEstimates,
  type ProjectEstimate,
  type ProjectRefLite,
} from "@/pages/admin-access/GroupAiTab";

const DEFAULT_LIMIT = 50;
/** The ceiling a sane handler would take. Asking for more is refused by
 *  the server, so this is the last page the screen can reach at all. */
export const MAX_LIMIT = 1000;

/** Parse the limit box. Returns the number, or `null` when it is out of
 *  range — the caller REPORTS that rather than substituting a default,
 *  which is the whole difference from a silently clamped input. */
export function parseLimit(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > MAX_LIMIT) return null;
  return n;
}

export function AccessAuditPage(): JSX.Element {
  const { t, locale } = useT();

  const [form, setForm] = useState({
    action: "",
    actorEmail: "",
    targetId: "",
    limit: String(DEFAULT_LIMIT),
  });
  const [applied, setApplied] = useState({
    action: "",
    actorEmail: "",
    targetId: "",
    limit: DEFAULT_LIMIT,
  });
  const [entries, setEntries] = useState<AccessAuditOut[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<AccessAuditOut | null>(null);

  const actions = useMemo(() => auditActionDict(t), [t]);
  const actors = useMemo(() => actorKindDict(t), [t]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const out = await accessAuditApi.list({
        action: applied.action === "" ? undefined : applied.action,
        actorEmail:
          applied.actorEmail === "" ? undefined : applied.actorEmail,
        targetId: applied.targetId === "" ? undefined : applied.targetId,
        limit: applied.limit,
      });
      setEntries(out.entries);
      setTotal(out.total);
    } catch (err) {
      // Shown whole rather than translated into "no hay registros": an
      // empty table would say the trail is empty, which is a different
      // and false statement. That mattered most when this was always the
      // 404 of a route that had not shipped; it still matters, because
      // the failures left are a 403 from `RequireManageUsers` and a 422
      // from a filter the server refused — and neither means "nothing
      // happened".
      setEntries(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = (): void => {
    const limit = parseLimit(form.limit);
    if (limit === null) {
      setError(t("access.audit.limitRange", { max: MAX_LIMIT }));
      return;
    }
    setError(null);
    setApplied({
      action: form.action.trim(),
      actorEmail: form.actorEmail.trim(),
      targetId: form.targetId.trim(),
      limit,
    });
  };

  const columns: Column<AccessAuditOut>[] = [
    {
      id: "when",
      header: t("access.audit.col.when"),
      width: "w-48",
      cell: (row) => (
        <DateCell
          value={row.created_at}
          locale={locale}
          mode="absoluteWithTime"
          invalidHint={t("access.common.badDate")}
        />
      ),
    },
    {
      id: "who",
      header: t("access.audit.col.who"),
      cell: (row) => (
        <span className="block truncate font-mono text-[13px] text-neutral-700">
          {row.actor_email}
        </span>
      ),
    },
    {
      id: "actor",
      header: t("access.audit.col.actorKind"),
      width: "w-44",
      cell: (row) => (
        <DictChip value={row.actor_kind} dictionary={actors} t={t} />
      ),
    },
    {
      id: "action",
      header: t("access.audit.col.action"),
      width: "w-56",
      cell: (row) => (
        <DictChip value={row.action} dictionary={actions} t={t} />
      ),
    },
    {
      id: "target",
      header: t("access.audit.col.target"),
      cell: (row) => (
        <span className="block truncate text-[13px] text-neutral-700">
          <span className="text-neutral-500">{row.target_kind}</span>{" "}
          <span className="font-mono">{row.target_id}</span>
        </span>
      ),
    },
    {
      id: "detail",
      header: t("access.audit.col.detail"),
      width: "w-32",
      cell: (row) => (
        <Button ghost size="xs" onClick={() => setOpen(row)}>
          {t("access.audit.view")}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer wide>
      <PageHeader
        title={t("access.audit.title")}
        subtitle={t("access.audit.subtitle")}
      />
      <AccessTabs active="audit" />

      <Callout tone="info" title={t("access.audit.startsHereTitle")}>
        {t("access.audit.startsHere")}
      </Callout>

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Input
          label={t("access.audit.filter.action")}
          value={form.action}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({ ...f, action: e.target.value }))
          }
        />
        <Input
          label={t("access.audit.filter.actor")}
          value={form.actorEmail}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({ ...f, actorEmail: e.target.value }))
          }
        />
        <Input
          label={t("access.audit.filter.target")}
          value={form.targetId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({ ...f, targetId: e.target.value }))
          }
        />
        <Input
          label={t("access.audit.filter.limit")}
          value={form.limit}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((f) => ({ ...f, limit: e.target.value }))
          }
        />
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Filter size={14} />}
          onClick={apply}
        >
          {t("access.audit.apply")}
        </Button>
      </div>

      <Section flush>
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-neutral-500">
            <Spinner size={16} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </div>
        ) : entries === null ? null : (
          <DataTable
            columns={columns}
            rows={entries}
            rowKey={(row) => row.id}
            caption={t("access.audit.title")}
            footnote={
              entries !== null && entries.length >= applied.limit
                ? t("access.audit.atCap", { limit: applied.limit, total })
                : undefined
            }
            empty={
              <EmptyState
                icon={<ScrollText size={24} />}
                title={t("access.audit.empty.title")}
                description={t("access.audit.empty.desc")}
              />
            }
          />
        )}
      </Section>

      <InsightCostSection />

      <AuditDetailModal entry={open} onClose={() => setOpen(null)} />
    </PageContainer>
  );
}

/**
 * What per-scope insights cost this tenant, per month (Plan J, J4.8 —
 * mockup 7).
 *
 * WHY IT IS ON THE AUDIT PAGE AND NOT SOMEWHERE OF ITS OWN
 * -------------------------------------------------------
 * Because it is the same question the rest of this screen answers, one
 * step further out. The trail says *who changed the access map*; this
 * says *what the map now costs to narrate*. An analyst who has just
 * turned three groups' insights on reads the consequence here, on the
 * page they are already on, instead of visiting three group tabs and
 * adding the numbers up by hand.
 *
 * THE TABLE IS IMPORTED, NOT REBUILT
 * ----------------------------------
 * `InsightCostTable` and the arithmetic beside it live in `GroupAiTab`,
 * where the switch that causes the cost lives, and this page renders the
 * same component over every project of the tenant instead of the
 * group's own subset. A second copy here is precisely how two screens
 * start quoting two different figures for one month — and it is also
 * where the word «estimación» would go missing from one of them.
 *
 * IT DOES NOT LOAD ITSELF
 * -----------------------
 * `runs_per_month` is REQUIRED by the server and has no default,
 * because insights regenerate on a data swap and not on a calendar: how
 * often a project reloads is a fact about the client's loader schedule
 * that only the operator knows. So nothing is fetched until somebody
 * answers, and one call per project is a deliberate consequence of the
 * endpoint being per project — not a loop worth hiding behind a
 * spinner on page load.
 */
export function InsightCostSection(): JSX.Element {
  const { t, locale } = useT();
  const [projects, setProjects] = useState<ProjectRefLite[] | null>(null);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [runs, setRuns] = useState("");
  const [priced, setPriced] = useState<{ runs: number; rows: ProjectEstimate[] } | null>(
    null,
  );
  const [pricing, setPricing] = useState(false);
  const bases = useMemo(() => estimateBasisDict(t), [t]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const out = await projectsApi.list();
        if (!alive) return;
        setProjects(
          out.projects.map((p) => ({
            slug: p.slug,
            label:
              resolveLocalized(
                parseProjectName(p.name) as DisplayLabel,
                locale,
              ) || p.slug,
          })),
        );
      } catch (err) {
        if (!alive) return;
        // Surfaced, not swallowed: an empty project list would price the
        // whole tenant at zero, which is a number and not an absence.
        setProjectsError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      alive = false;
    };
    // A project name is a bilingual document, so the list is re-resolved
    // when the operator switches language rather than keeping whichever
    // half was rendered first.
  }, [locale]);

  const runsPerMonth = parseRunsPerMonth(runs);

  const price = async (): Promise<void> => {
    if (runsPerMonth === null || projects === null) return;
    const at = runsPerMonth;
    setPricing(true);
    try {
      setPriced({ runs: at, rows: await loadProjectEstimates(projects, at) });
    } finally {
      setPricing(false);
    }
  };

  const rows = currentEstimate(priced, runsPerMonth);
  const split = rows === null ? null : splitEstimates(rows);
  const total = split === null ? 0 : currentMonthlyCost(split.ok);

  return (
    <Section
      title={t("access.ai.cost.title")}
      description={t("access.ai.cost.tenantDesc")}
    >
      {projectsError !== null && (
        <Callout tone="danger" title={t("access.ai.cost.projectsFailedTitle")}>
          <span className="whitespace-pre-wrap">{projectsError}</span>
        </Callout>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <RunsPerMonthField
          value={runs}
          onChange={setRuns}
          disabled={projects === null}
          t={t}
        />
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Calculator size={14} />}
          disabled={runsPerMonth === null || projects === null || pricing}
          onClick={() => void price()}
        >
          {t("access.ai.cost.compute")}
        </Button>
        {pricing && (
          <span className="inline-flex items-center gap-2 pb-2 text-sm text-neutral-600">
            <Spinner size={14} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </span>
        )}
      </div>

      {rows === null || split === null ? (
        <p className="mt-3 text-[13px] text-neutral-600">
          {t("access.ai.cost.notYet")}
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-neutral-900">
            {t("access.ai.cost.tenantTotal", { n: total })}{" "}
            <DictChip value={ESTIMATE_BASES[0]} dictionary={bases} t={t} />
          </p>
          {split.failed.length > 0 && (
            <p className="text-[13px] text-neutral-700">
              {t("access.ai.cost.partial", { n: split.failed.length })}
            </p>
          )}
          <InsightCostTable
            rows={rows}
            emptyLabel={t("access.ai.cost.noProjectsTenant")}
            t={t}
          />
        </div>
      )}
    </Section>
  );
}

/**
 * The before/after pair, pretty-printed.
 *
 * `null` renders as a SENTENCE, never as `{}`: a create has no before
 * and a delete has no after, and an empty object would claim something
 * existed and was empty.
 */
export function AuditDetailModal({
  entry,
  onClose,
}: {
  entry: AccessAuditOut | null;
  onClose: () => void;
}): JSX.Element | null {
  const { t } = useT();
  if (entry === null) return null;
  return (
    <Modal
      open
      onClose={onClose}
      title={t("access.audit.detailTitle")}
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button variant="primary" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <dl className="grid gap-2 sm:grid-cols-2">
          <div>
            <dt className="text-[13px] font-semibold text-neutral-600">
              {t("access.audit.col.action")}
            </dt>
            <dd className="font-mono text-sm text-neutral-900">
              {entry.action}
            </dd>
          </div>
          <div>
            <dt className="text-[13px] font-semibold text-neutral-600">
              {t("access.audit.col.target")}
            </dt>
            <dd className="font-mono text-sm text-neutral-900">
              {entry.target_kind} / {entry.target_id}
            </dd>
          </div>
          <div>
            <dt className="text-[13px] font-semibold text-neutral-600">
              {t("access.audit.col.who")}
            </dt>
            <dd className="font-mono text-sm text-neutral-900">
              {entry.actor_email}
            </dd>
          </div>
          <div>
            <dt className="text-[13px] font-semibold text-neutral-600">
              {t("access.audit.actorId")}
            </dt>
            <dd className="font-mono text-sm text-neutral-900">
              {entry.actor_user_id === null
                ? t("access.audit.actorGone")
                : entry.actor_user_id}
            </dd>
          </div>
        </dl>

        <JsonBlock
          title={t("access.audit.before")}
          value={entry.before_json}
          absent={t("access.audit.noBefore")}
        />
        <JsonBlock
          title={t("access.audit.after")}
          value={entry.after_json}
          absent={t("access.audit.noAfter")}
        />
      </div>
    </Modal>
  );
}

export function JsonBlock({
  title,
  value,
  absent,
}: {
  title: string;
  value: Record<string, unknown> | null;
  absent: string;
}): JSX.Element {
  return (
    <div>
      <p className="text-[13px] font-semibold text-neutral-600">{title}</p>
      {value === null ? (
        <p className="text-[13px] text-neutral-600">{absent}</p>
      ) : (
        <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-neutral-900 p-3 font-mono text-xs text-neutral-100">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}
